import { ApiError, apiDelete, apiGet, apiPost, apiUploadPut } from '../client';
import { listTables } from './tables';
import {
  BusinessFloorPlanSchema,
  EmptyResponseSchema,
  FloorPlanAnalysisResponseSchema,
  PublicFloorReadSchema,
  type BusinessFloorPlan,
  type FloorPlanAnalysisResponse,
  type PublicFloorRead,
} from '../schemas';

/** Public floor read; the roster uses it so employees never need owner-only table access. */
export function getPublicFloor(businessId: string): Promise<PublicFloorRead> {
  return apiGet(`/v1/businesses/${encodeURIComponent(businessId)}/floor`, PublicFloorReadSchema);
}

/** Owner-only floor-plan image (`business_floor_plans`, migration 0026). */
export function getFloorPlan(): Promise<BusinessFloorPlan> {
  return apiGet('/v1/business/floor-plan', BusinessFloorPlanSchema);
}

/** Owners can see draft floors; employee access stays on the public read. */
export async function getRosterFloor(businessId: string, owner: boolean): Promise<PublicFloorRead> {
  if (!owner) {
    return getPublicFloor(businessId).catch((error: unknown) => {
      if (error instanceof ApiError && error.status === 404) return { floorPlan: null, resources: [] };
      throw error;
    });
  }
  const [floorPlan, tables] = await Promise.all([getFloorPlan(), listTables(false)]);
  return { floorPlan, resources: tables.data.map(table => ({
    id: table.id, name: table.name, seats: table.seats, kind: table.kind, isPrivate: table.isPrivate,
    x: table.gridX, y: table.gridY, w: table.gridW, h: table.gridH,
    shape: table.shape, rotation: table.rotation, available: true,
  })) };
}

export type PutFloorPlanInput = {
  /** Already-sanitized image bytes; the server re-validates format and dimensions. */
  blob: Blob;
  width: number;
  height: number;
};

export function putFloorPlan(
  input: PutFloorPlanInput,
  idempotencyKey: string,
): Promise<BusinessFloorPlan> {
  const form = new FormData();
  form.append('image', input.blob, 'floor-plan.jpg');
  form.append('meta', JSON.stringify({ width: input.width, height: input.height }));
  return apiUploadPut('/v1/business/floor-plan', BusinessFloorPlanSchema, form, idempotencyKey);
}

export function deleteFloorPlan(idempotencyKey: string): Promise<undefined> {
  return apiDelete('/v1/business/floor-plan', EmptyResponseSchema, idempotencyKey);
}

/**
 * Sends the stored floor-plan image to Gemini vision and returns PROPOSED
 * resources. Never writes a row server-side — commit any of these through
 * `createTable`/the normal mesas CRUD. No Idempotency-Key: this is a
 * read-shaped propose, not a mutation.
 */
export function analyzeFloorPlan(): Promise<FloorPlanAnalysisResponse> {
  return apiPost('/v1/business/floor-plan/analyze', FloorPlanAnalysisResponseSchema);
}
